/* eslint-disable max-len */
import React, {
    useEffect, useState, useMemo, useCallback, useContext
} from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { messages as commonMessages } from 'gw-platform-translations';
import { error } from '@jutro/logger';
import _ from 'lodash';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import {
    Loader,
    InfoLabel,
    ModalNextProvider,
    Link,
    Icon
} from '@jutro/components';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import { UsersProfileDetailsService } from 'gw-capability-profileinfo';
import { PolicyService } from 'gw-capability-policy';
import { DocumentService } from 'gw-capability-policydocument';
import { RiskManagementNotifications } from 'tdic-capability-risk-management-react';
import { CMSComponent, withApplicationPropertyContext, EmailNotificationComponent } from 'tdic-components-platform-react';
import {
    Currency as CurrencyField, ImageComponent
} from 'gw-components-platform-react';
import { isCapabilityEnabled } from 'gw-portals-config-js';
import { useAuthentication } from 'gw-digital-auth-react';
import { LobIconUtil } from 'gw-portals-util-js';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';

// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

import { PaperlessComponent, AutopayComponent } from 'tdic-capability-policy-common-react';
import styles from './LandingPage.module.scss';
import metadata from './LandingPage.metadata.json5';
import messages from './LandingPage.messages';

const getCellPolicyType = (rowData) => {
    const activePeriod = _.first(rowData.periods);
    const offeringCode = _.get(rowData, 'offeringCode', '');
    const lineOfBusiness = activePeriod.lines.length > 1
        ? activePeriod.lines.map((line) => line) : _.first(activePeriod.lines);
    return (
        <div className={styles.policyTypeCell}>
            <ImageComponent
                src={`/branding/${LobIconUtil.getImageIcon(lineOfBusiness, offeringCode)}.svg`}
                alt={lineOfBusiness}
                title={rowData.offeringName}
            />
            <span>{_.get(appConfig, `TDICAbbreviations.${rowData.offeringCode}`, '')}</span>
        </div>
    );
};

const isPolicyActive = (periods) => {
    const activePeriod = _.first(periods);
    return activePeriod.status === 'In Force' && new Date(activePeriod.effective).getTime() < new Date().getTime();
};

const isPolicyExpired = (periods) => {
    const activePeriod = _.first(periods);
    return activePeriod.status === 'Expired' || activePeriod.status === 'Canceled'
        || activePeriod.status === 'Non-renewed';
};

const getCellStatus = (rowData) => {
    if (isPolicyExpired(rowData.periods)) {
        return <InfoLabel type="info" className={styles.expired} size="medium" message={messages.statusExpired} />;
    }
    if (isPolicyActive(rowData.periods)) {
        return <InfoLabel type="success" size="medium" message={messages.statusActive} />;
    }

    return <InfoLabel type="info" size="medium" message={messages.statusOffered} />;
};

const getCellPolicy = (rowData) => {
    const activePeriod = _.first(rowData.periods);
    const periodWithIDCard = rowData.periods.find((policy) => policy.idCardPublicID);
    return (
        <Link
            className={styles.link}
            title={rowData.offeringName}
            to={{
                pathname: `/account-policy-details/${activePeriod.policyId}`,
                productCode: activePeriod.lines[0],
                idCard: periodWithIDCard ? {
                    idCardSessionID: _.get(periodWithIDCard, 'idCardSessionID'),
                    idCardPublicID: _.get(periodWithIDCard, 'idCardPublicID')
                } : null
            }}
        >
            {activePeriod.policyId}
        </Link>
    );
};

const getFormattedDate = (date) => {
    return moment(new Date(date)).format('MM/DD/YY');
};

const getCellEffective = (rowData) => {
    const activePeriod = _.first(rowData.periods);
    const effectiveDate = getFormattedDate(activePeriod.effective);
    const expirationDate = getFormattedDate(activePeriod.expiration);
    return (
        <span>{`${effectiveDate} - ${expirationDate}`}</span>
    );
};

const getCellPremium = (rowData, rowIndex) => {
    const activePeriod = _.first(rowData.periods);
    return (
        <CurrencyField
            id={`premium_${rowIndex}`}
            value={activePeriod.premium}
            dataType="object"
            readOnly
            hideLabel
            className={styles.policyTableCell}
        />
    );
};

function LandingPage(props) {
    const { isAuthorisedUser, tncTransactionTypesSubTypes, termData: terms } = props;
    const translator = useContext(TranslatorContext);
    const [accountBillingData, setAccountBillingData] = useState([]);
    const [accountPolicyData, setAccountPolicyData] = useState([]);
    const { authHeader, user_name: userName, email: userEmail } = useAuthentication();
    const [rmExpiringData, updateRMExpiringData] = useState([]);
    const [renewalSurveyPolicy, updateRenewalSurveyPolicy] = useState(false);
    const [isLoading, setLoadingState] = useState(false);
    const [adBannerContent, setAdBannerContent] = useState([]);
    const { capabilitiesConfig } = appConfig;

    const processADContent = useCallback((adData) => {
        const adContent = _.sortBy(_.filter(adData, (ad) => {
            return ad.Status === 'active';
        }), ['Order']);
        setAdBannerContent(adContent);
    }, []);

    const updateNotificationData = useCallback((notification) => {
        const plPolicyChangeSurveyAvailable = _.get(_.find(notification, { menuOption: 'PLPolicyChangeSurvey' }), 'available', false);
        const policiesForAnestheticModalityAvailable = _.get(_.find(notification, { menuOption: 'PoliciesForAnestheticModalityQuestionnaire' }), 'available', false);
        const isPLSurveyAvailable = plPolicyChangeSurveyAvailable
            || policiesForAnestheticModalityAvailable;
        updateRenewalSurveyPolicy(isPLSurveyAvailable); const riskManagementCourses = _.find(notification, { menuOption: 'ExpiringRiskManagementCourses' });
        if (_.get(riskManagementCourses, 'available', false)) {
            const courseExpireData = _.get(riskManagementCourses, 'values', []);
            updateRMExpiringData(courseExpireData);
        }
    }, []);

    useEffect(() => {
        const { history } = props;
        setLoadingState(true);
        // Create a variable to store timeouts if this component becomes unmounted.
        const runningTimeouts = [];
        let billingPromise = Promise.resolve([]);
        if (isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' })) {
            billingPromise = AccountBillingDetailsService.getAccountBillingSummary(authHeader);
        }
        if (!isAuthorisedUser) {
            const doUsrNotifAct = () => {
                UsersProfileDetailsService.getUserNotifications(authHeader)
                    .then((response) => {
                        updateNotificationData(response);
                    });
            };
            // If we came from the email confirmation screen, wait 5 seconds
            // before checking for the status of the notifications.
            if (history.fromEmailConf) {
                // This property was added ad-hoc so we're going to remove
                // it so that it does not trigger this again.
                delete history.fromEmailConf;
                runningTimeouts.push(setTimeout(doUsrNotifAct, 5000));
            } else {
                doUsrNotifAct();
            }
        }
        Promise.all([billingPromise.then(setAccountBillingData),
            PolicyService.getAccountPolicySummaries(authHeader).then(setAccountPolicyData),
            PolicyService.getAdBannerList().then((resp) => resp.json()).then(processADContent),

        ]).catch((err) => {
            error(`LandingPage:- getAccountPolicySummaries - Unable to get Account Policy Summaries ${JSON.stringify(err)}`);
            return ModalNextProvider.showAlert({
                title: commonMessages.genericError,
                message: commonMessages.genericErrorMessage,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).then(() => {
                history.push('/error');
            }, _.noop);
        }).finally(() => {
            setLoadingState(false);
        });
        // disable re render
        // eslint-disable-next-line react-hooks/exhaustive-deps

        // return an expression to cancel running timeouts
        return () => {
            runningTimeouts.forEach((t) => clearTimeout(t));
        };
    }, [authHeader, capabilitiesConfig, isAuthorisedUser, processADContent,
        props, updateNotificationData]);

    const updatePaperless = useCallback(async (event, rowData) => {
        event.preventDefault();
        const activePeriod = _.first(rowData.periods);
        const componentProps = {
            authHeader: authHeader,
            policyNumber: _.get(activePeriod, 'policyId'),
            offeringName: _.get(rowData, 'offeringName'),
            pniEmail: _.get(activePeriod, 'pniEmail'),
            userName: userName,
            email: userEmail,
            tncTransactionTypesSubTypes: tncTransactionTypesSubTypes,
            terms: terms
        };
        const result = await ModalNextProvider.showModal(
            <PaperlessComponent {...componentProps} />
        ).catch(_.noop);

        if (result) {
            if (result && result === true) {
                setLoadingState(true);
                setTimeout(() => {
                    PolicyService.getAccountPolicySummaries(authHeader).then((data) => {
                        setAccountPolicyData(data);
                        setLoadingState(false);
                    });
                }, 4000);
            } else {
                return false;
            }
        }
        return false;
    }, [authHeader, userEmail, terms, tncTransactionTypesSubTypes, userName]);

    const updateAutoPay = useCallback(async (event, rowData) => {
        event.preventDefault();
        const activePeriod = _.first(rowData.periods);
        const componentProps = {
            authHeader: authHeader,
            policyNumber: _.get(activePeriod, 'policyId'),
            offeringName: _.get(rowData, 'offeringName'),
            userName: userName,
            email: userEmail,
            tncTransactionTypesSubTypes: tncTransactionTypesSubTypes,
            terms: terms
        };
        const result = await ModalNextProvider.showModal(
            <AutopayComponent {...componentProps} />
        ).catch(_.noop);

        if (result) {
            if (result && result === true) {
                setLoadingState(true);
                setTimeout(() => {
                    PolicyService.getAccountPolicySummaries(authHeader).then((data) => {
                        setAccountPolicyData(data);
                        setLoadingState(false);
                    });
                }, 4000);
            } else {
                return false;
            }
        }
        return false;
    }, [authHeader, userEmail, terms, tncTransactionTypesSubTypes, userName]);

    const getCellPaperless = useCallback((rowData) => {
        const activePeriod = _.first(rowData.periods);
        const isPaperlessEnabled = _.get(activePeriod, 'isPaperlessEnabled', false);
        if (!activePeriod.canBeChanged || isAuthorisedUser) {
            if (isPaperlessEnabled) {
                return 'Yes';
            }
            return 'No';
        }
        if (isPaperlessEnabled) {
            return 'Yes';
        }
        return (
            <Link onClick={(event) => updatePaperless(event, rowData)} href="/">
                {messages.turnOn}
            </Link>
        );
    }, [isAuthorisedUser, updatePaperless]);

    const getCellAutopay = useCallback((rowData) => {
        const activePeriod = _.first(rowData.periods);
        const isApwEnrolled = _.get(activePeriod, 'isApwEnrolled', false);
        const canChangePaymentPlan = _.get(activePeriod, 'canChangePaymentPlan', true);

        if (activePeriod.status === 'Expired' || activePeriod.status === 'Canceled'
            || activePeriod.status === 'Non-renewed' || isAuthorisedUser || !canChangePaymentPlan) {
            if (isApwEnrolled) {
                return 'Yes';
            }
            return 'No';
        }
        if (isApwEnrolled) {
            return 'Yes';
        }
        return (
            <Link onClick={(event) => updateAutoPay(event, rowData)} href="/">
                {messages.setUp}
            </Link>
        );
    }, [isAuthorisedUser, updateAutoPay]);

    const aggregateBilling = useCallback((invoices) => {
        if (invoices.length === 0) {
            return null;
        }

        let total = 0;
        let due = new Date(invoices[0].dueDate);

        _.each(invoices, (invoice) => {
            total += invoice.amountDue.amount;
            const newDue = new Date(invoice.dueDate);
            if (newDue.getTime() < due.getTime()) {
                due = newDue;
            }
        });

        return {
            amountDue: {
                amount: total,
                currency: invoices[0].amountDue.currency
            },
            earliestDue: due
        };
    }, []);

    const getMyBalanceOverDueAndCurrentData = useCallback(
        (invoiceStatus) => {
            if (accountBillingData.activeInvoices_Ext) {
                const invoices = accountBillingData.activeInvoices_Ext;
                return aggregateBilling(
                    invoices.filter((invoice) => invoice.invoiceStatus === invoiceStatus)
                );
            }
            return null;
        },
        [accountBillingData.activeInvoices_Ext, aggregateBilling]
    );

    const getBalanceData = useCallback(() => {
        const overdue = getMyBalanceOverDueAndCurrentData('due');
        const current = getMyBalanceOverDueAndCurrentData('billed');
        if (overdue && current) {
            return {
                amount: current.amountDue.amount + overdue.amountDue.amount,
                currency: current.amountDue.currency
            };
        }
        if (overdue || current) {
            return (overdue || current).amountDue;
        }
        return null;
    }, [getMyBalanceOverDueAndCurrentData]);

    const readValue = useCallback(
        (id, path) => {
            if (path === 'balanceAmount') {
                return getBalanceData();
            }
            if (path === 'overDueAmount') {
                return _.get(getMyBalanceOverDueAndCurrentData('due'), 'amountDue');
            }
            if (path === 'currentAmount') {
                return _.get(getMyBalanceOverDueAndCurrentData('billed'), 'amountDue');
            }
            if (path === 'dueDate') {
                return _.get(getMyBalanceOverDueAndCurrentData('due'), 'earliestDue');
            }
            return _.get(accountBillingData, path);
        },
        [accountBillingData, getBalanceData, getMyBalanceOverDueAndCurrentData]
    );

    const policyWithIDCard = useMemo(() => {
        let periodWithIDCard;
        accountPolicyData.some((policyData) => {
            return policyData.periods.some((period) => {
                if (period.idCardPublicID) {
                    periodWithIDCard = period;
                    return true;
                }
                return false;
            });
        });
        return periodWithIDCard;
    }, [accountPolicyData]);

    const getPolicyData = useCallback(() => {
        return accountPolicyData;
    }, [accountPolicyData]);

    const getIDCardQuickLink = useMemo(() => {
        if (!policyWithIDCard) {
            return null;
        }
        return DocumentService.downloadPolicyDocument(
            policyWithIDCard.idCardPublicID,
            policyWithIDCard.idCardSessionID
        );
    }, [policyWithIDCard]);

    const handleManagePaymentLinkClick = useCallback(
        () => {
            const { history } = props;
            return history.push({
                pathname: 'billing-summary',
                state: {
                    managePaymentPlan: true
                }
            });
        },
        [props]
    );

    const mailToTrigger = useCallback((event, email) => {
        event.preventDefault();
        window.location = `mailto:${email}`;
    }, []);

    const claimCapabilityCheck = useMemo(() => {
        return isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'fnol' });
    }, [capabilitiesConfig]);

    const getCellAgent = useCallback(
        (rowData) => {
            return (
                <>
                    <Link className={styles.link} onClick={(e) => mailToTrigger(e, rowData.producerContact.email)} href="/">
                        {rowData.producerContact.displayName}
                    </Link>
                    <br />
                    {rowData.producerContact.phoneNumber ? (
                        <span>
                            {rowData.producerContact.phoneNumber}
                        </span>
                    ) : (
                        ''
                    )}
                </>
            );
        },
        [mailToTrigger]
    );

    const getPrintDeclarationPageCell = useCallback((rowData) => {
        const activePeriod = _.first(rowData.periods);
        if (activePeriod.status !== 'In Force' || !activePeriod.canBeChanged || isAuthorisedUser || !activePeriod.decPageGenAvailability) {
            return null;
        }
        return (
            <Link
                className={styles.link}
                title={rowData.offeringName}
                to={{
                    pathname: `/printdecPage/${activePeriod.policyId}`,
                    state: { offeringName: rowData.offeringName }
                }}
            >
                <Icon icon="mi-print" />
            </Link>
        );
    }, [isAuthorisedUser]);

    const getTableOverrides = useCallback(() => {
        return {
            policyTypeColumn: { onCell: getCellPolicyType },
            statusColumn: { onCell: getCellStatus },
            policyColumn: { onCell: getCellPolicy },
            effectiveColumn: { onCell: getCellEffective },
            premiumColumn: { onCell: getCellPremium },
            APWColumn: { onCell: getCellAutopay },
            paperlessColumn: { onCell: getCellPaperless },
            // eslint-disable-next-line no-use-before-define
            declarationPagePrintColumn: {
                onCell: getPrintDeclarationPageCell,
                visible: !isAuthorisedUser
            },
            agentColumn: {
                onCell: getCellAgent
            }
        };
    }, [getCellAutopay, getCellPaperless, getPrintDeclarationPageCell, isAuthorisedUser, getCellAgent]);

    const getDeliquency = useCallback(() => {
        return (
            <FormattedMessage
                {...messages.delinquencyStatusMsg}
                values={{
                    link: <Link to="/make-payment">{translator(messages.delinquencyStatus)}</Link>
                }}
            />
        );
    }, [translator]);

    const onFileAClaimQuickLinkClick = useCallback(
        () => () => {
            const { history } = props;
            return history.push({
                pathname: '/fnol-select-policy',
                state: {
                    redirectPath: '/home',
                    claimData: { lossDate: new Date() }
                }
            });
        },
        [props]
    );

    const gotoTDICFAQ = useMemo(() => {
        return _.get(appConfig, `tdic.portalFAQURL.${_.toLower(process.env.NODE_ENV)}`, _.get(appConfig, 'tdic.portalFAQURL.development'));
    },
    []);

    const handleLinkClick = useCallback(
        (event) => {
            const { history } = props;
            return history.push(event.path);
        },
        [props]
    );


    const showBillingSection = useMemo(() => {
        return isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' });
    }, [capabilitiesConfig]);

    const getPolicyQuestionnaireMessage = useCallback(() => {
        return (
            <FormattedMessage
                {...messages.renewalSurveyNotify}
                values={{
                    policyQuestionnaireLink: <Link to="/pl-policy-questionnaire">{translator(messages.policyQuestionnaire)}</Link>
                }}
            />
        );
    }, [translator]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const override = {
        '@field': {
            phone: {
                labelPosition: 'left'
            }
        },
        myBalanceOverdueAmount: {
            visible: !_.isEmpty(getMyBalanceOverDueAndCurrentData('due'))
        },
        dueDateContainer: {
            visible: !_.isEmpty(getMyBalanceOverDueAndCurrentData('due'))
        },
        dueDateLabel: {
            visible: !_.isEmpty(getMyBalanceOverDueAndCurrentData('billed'))
        },
        policiesTable: {
            data: getPolicyData()
        },
        printAnIDCardLink: {
            visible: false,
            href: getIDCardQuickLink,
            target: '_blank'
        },
        fileAClaimLink: {
            onClick: onFileAClaimQuickLinkClick(),
            visible: claimCapabilityCheck && !isAuthorisedUser
        },
        gotoTDICFAQ: {
            href: gotoTDICFAQ
        },
        tileGroupContainer: {
            visible: showBillingSection
        },
        makeAPaymentLink: {
            visible: showBillingSection
        },
        rmNotification: {
            notifications: !_.isEmpty(rmExpiringData) ? rmExpiringData : [],
            visible: !isAuthorisedUser
        },
        surveyNotification: {
            message: getPolicyQuestionnaireMessage(),
            visible: renewalSurveyPolicy && !isAuthorisedUser
        },
        deliquency: {
            visible: accountBillingData.delinquencyStatus === 'Open',
            message: getDeliquency()
        },
        adBanner: {
            bannerList: adBannerContent
        },
        managePaymentsLink: {
            visible: !isAuthorisedUser
        },
        gotoTDICRequestAQuote: {
            visible: !isAuthorisedUser
        },
        declarationPagePrintColumn: {
            visible: !isAuthorisedUser
        },
        ...getTableOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveValue: readValue,
        resolveCallbackMap: {
            handleLinkClick,
            handleManagePaymentLinkClick
        },
        resolveComponentMap: {
            rmnotificationcomponent: RiskManagementNotifications,
            cmscomponent: CMSComponent,
            emailnotificationcomponent: EmailNotificationComponent
        }
    };

    return renderContentFromMetadata(metadata.pageContent, override, resolvers);
}

export default withRouter(withApplicationPropertyContext(LandingPage));
